import './SimpleIceBreaker.pcss';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { useIceBreakerCRUDActions } from '@mm/state/actions/IceBreakerCRUDActions';
import {
  ButtonType,
  GenericButtonModel,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { InputWithEmoji } from '../../../../common/components/InputWithEmoji/InputWithEmoji';
import { MAX_TEXT_WIDGET_LENGTH } from '@mm/constants/widgets';
import { BubbleTextInput } from '../../../../common/widgets/common/BubbleTextInput/BubbleTextInput';
import { OnClickElsewhere } from '../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { useButtonHandlers } from '../../../../common/helpers/ice-breaker-helpers/use-button-handlers';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import WidgetModel from '@mm/state/model/WidgetModel';
import { AddButton } from './AddButton';
import { ICE_BREAKER_BUTTONS_LIMIT } from './constants';
import { makeButton } from '../../build/requestModels/widgets/buttonStackWidget/Button';
import { useTextWidgetHandlers } from '../../../../common/helpers/widget-helpers/use-text-widget-handlers';
import { ReactComponent as FancyTrashIcon } from 'assets/garbage.svg';

type Props = {
  token: string;
  botId: number;
  onValidate: (valid: boolean) => void;
};

type PageButton = GenericButtonModel<PageButtonConfig>;

const validAnswer = (button: PageButton, answer?: string) =>
  answer?.trim() && button.config.pageId;
const validQuestion = ({ name }: PageButton) => !!name?.trim();

type ButtonProps = {
  button: PageButton;
  index: number;
  onFocus: () => void;
  onBlur: () => void;
  focused: boolean;
  onDelete: () => void;
  answer: string;
  onUpdateName: (name: string) => void;
  onUpdateAnswer: (answer: string) => void;
};

const Button = ({
  button,
  index,
  onFocus,
  onBlur,
  focused,
  onDelete,
  answer,
  onUpdateName,
  onUpdateAnswer,
}: ButtonProps) => {
  const [question, setQuestion] = useState(button.name || '');
  const [questionFocused, setQuestionFocused] = useState(false);
  const [answerFocused, setAnswerFocused] = useState(false);
  return (
    <OnClickElsewhere
      key={index}
      className="simple-ice-breaker__button-wrapper"
      handleClick={onBlur}
    >
      <div
        className={classNames('simple-ice-breaker__button', {
          'simple-ice-breaker__button--active': focused,
        })}
        onClick={onFocus}
      >
        <div className="simple-ice-breaker__delete-button-container">
          <span
            className="simple-ice-breaker__delete-button"
            onClick={onDelete}
          >
            <FancyTrashIcon className="simple-ice-breaker__delete-icon" />
          </span>
        </div>
        <div className="simple-ice-breaker__question-container">
          <div className="simple-ice-breaker__question-label">
            Icebreaker Question {index + 1}
          </div>
          <InputWithEmoji
            className={classNames('simple-ice-breaker__question', {
              'simple-ice-breaker__question--error': !validQuestion(button),
              'simple-ice-breaker__question--focused': questionFocused,
            })}
            textInputClassName="simple-ice-breaker__question-input"
            value={question}
            onBlur={() => {
              setQuestionFocused(false);
              if (button.name !== question) {
                onUpdateName(question);
              }
            }}
            onFocus={() => setQuestionFocused(true)}
            onChange={value => setQuestion(value)}
          />
        </div>
        <div className="simple-ice-breaker__answer-container">
          <div className="simple-ice-breaker__answer-label">
            Respond with the following Text
          </div>
          <OnClickElsewhere handleClick={() => setAnswerFocused(false)}>
            <BubbleTextInput
              className={classNames('simple-ice-breaker__answer-input', {
                'simple-ice-breaker__answer-input--focused': answerFocused,
                'simple-ice-breaker__answer-input--error': !validAnswer(
                  button,
                  answer,
                ),
              })}
              updateText={newText => onUpdateAnswer(newText)}
              value={answer}
              editMode={true}
              onClick={() => setAnswerFocused(true)}
              onBlur={() => setAnswerFocused(false)}
              maxLength={MAX_TEXT_WIDGET_LENGTH}
            />
          </OnClickElsewhere>
        </div>
      </div>
    </OnClickElsewhere>
  );
};

export const SimpleIceBreaker = ({ token, botId, onValidate }: Props) => {
  const iceBreaker = useSelector(
    (state: StoreState) => state.iceBreakers.facebook.entities[0],
  );
  const iceBreakerCRUDActions = useIceBreakerCRUDActions();
  const [focusedButtonId, setFocusedButtonId] = useState<number | undefined>();
  const [widgetsForButtons, setWidgetsForButtons] = useState<{
    [id: string]: WidgetModel;
  }>({});
  const buttonHandlers = useButtonHandlers();
  const textWidgetHandlers = useTextWidgetHandlers();
  const widgetCRUDActions = useWidgetCRUDActions();

  useEffect(() => {
    if (iceBreaker?.botId !== botId) {
      iceBreakerCRUDActions.retrieveAll(botId, 'facebook', token);
    }
  }, [botId]);
  useEffect(() => {
    if (iceBreaker) {
      const valid = iceBreaker.buttons.every(
        button =>
          validQuestion(button) && validAnswer(button, buttonAnswer(button)),
      );
      onValidate(valid);
    } else {
      onValidate(false);
    }
  }, [iceBreaker, widgetsForButtons]);

  useEffect(() => {
    if (iceBreaker) {
      (async () => {
        const toCheck = iceBreaker.buttons.filter(
          ({ id }) => !widgetsForButtons[id],
        );
        if (!toCheck.length) {
          return;
        }
        const pageIds = toCheck.map(({ config: { pageId } }) => pageId);
        const allWidgets: WidgetModel[][] = await Promise.all(
          pageIds.map(pageId => widgetCRUDActions.retrieveAll(pageId)),
        );
        const newWidgetsForButtons = allWidgets.reduce((acc, widgets) => {
          const button = toCheck.find(
            button => button.config.pageId === widgets[0]?.pageId,
          );
          if (!button) {
            return acc;
          }
          acc[button.id] = widgets.sort(w => w.sortOrder)[0];
          return acc;
        }, {} as { [id: string]: WidgetModel });
        setWidgetsForButtons({ ...widgetsForButtons, ...newWidgetsForButtons });
      })();
    }
  }, [iceBreaker]);

  const deleteButton = (button: PageButton) => {
    buttonHandlers.delete(iceBreaker.botId, iceBreaker.id, 'facebook', button);
  };

  const createButton = async () => {
    if (!iceBreaker) {
      return;
    }
    const maxSortOrder = Math.max(
      ...iceBreaker.buttons.map(b => b.sortOrder),
      -1,
    );
    const buttonIndex = maxSortOrder + 1;
    const updatedButton = makeButton({
      type: ButtonType.PAGE,
      name: '',
      sortOrder: buttonIndex,
    }) as PageButton;
    const newButton = await buttonHandlers.createButton(
      iceBreaker,
      updatedButton,
      false,
    );
    if (!newButton) {
      return;
    }
    setFocusedButtonId(newButton.id);
    const widget = await textWidgetHandlers.addNew(newButton.config.pageId, 1);
    setWidgetsForButtons({ ...widgetsForButtons, [newButton.id]: widget });
  };

  const updateButtonName = (button: PageButton, name: string) => {
    buttonHandlers.updateButton(iceBreaker, { ...button, name });
  };

  const updateButtonAnswer = (button: PageButton, text: string) => {
    const widget = widgetsForButtons[button.id];
    const updatedWidget = {
      ...widget,
      config: { ...widget.config, body: text },
    };
    setWidgetsForButtons({ ...widgetsForButtons, [button.id]: updatedWidget });
    widgetCRUDActions.update(updatedWidget);
  };

  const buttonAnswer = (button: PageButton) =>
    widgetsForButtons[button.id]?.config?.body;

  if (!iceBreaker) {
    return null;
  }

  return (
    <div className="simple-ice-breaker">
      {iceBreaker.buttons.map((button, index) => (
        <Button
          button={button}
          index={index}
          key={button.id}
          focused={button.id === focusedButtonId}
          answer={buttonAnswer(button) || ''}
          onDelete={() => deleteButton(button)}
          onBlur={() => setFocusedButtonId(undefined)}
          onFocus={() => setFocusedButtonId(button.id)}
          onUpdateName={(name: string) => updateButtonName(button, name)}
          onUpdateAnswer={(answer: string) =>
            updateButtonAnswer(button, answer)
          }
        />
      ))}
      <AddButton
        className="simple-ice-breaker__add-button"
        onClick={createButton}
        disabled={iceBreaker.buttons.length >= ICE_BREAKER_BUTTONS_LIMIT}
      />
    </div>
  );
};
